import {
  APPROVAL_TYPE_LABELS,
  ATTENDANCE_STATUS_CATALOG,
  CORRECTION_REASON_CATALOG,
  RISK_FLAG_CATALOG,
} from '@micarahr/shared-types';
import { formatTime, humanize } from './format';
import { todayInBusiness } from './business-date';

/**
 * Words for the codes the API stores.
 *
 * `humanize` turns `pending_hr` into "Pending hr" — legible to the person who
 * wrote the enum and to nobody else. Each vocabulary a reviewer reads is named
 * here once, from the shared catalogue where one exists, so the approvals
 * queue, the leave and overtime queues and the corrections queue say the same
 * thing about the same request. An unknown code still falls back to
 * `humanize` rather than to nothing: a new status should look unpolished, not
 * missing.
 */

const lookup = (table: Record<string, string>, code: string | null | undefined) =>
  code ? (table[code] ?? humanize(code)) : '—';

/** Leave, overtime and correction request statuses. */
const REQUEST_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  pending: 'Waiting for a decision',
  pending_manager: 'Waiting for manager',
  pending_hr: 'Waiting for HR',
  approved: 'Approved',
  rejected: 'Rejected',
  returned: 'Returned for changes',
  cancelled: 'Cancelled',
  withdrawn: 'Withdrawn',
};

export const requestStatusLabel = (status: string | null | undefined) =>
  lookup(REQUEST_STATUS_LABELS, status);

/**
 * Overtime types. The stored codes are the phone's list, which predates the
 * shared catalogue's names — both spellings are here.
 */
const OVERTIME_TYPE_LABELS: Record<string, string> = {
  regular_day: 'Regular day',
  rest_day: 'Rest day',
  holiday: 'Holiday',
  regular_holiday: 'Regular holiday',
  special_non_working: 'Special non-working day',
  night: 'Night work',
  night_work: 'Night work',
  emergency: 'Emergency work',
  emergency_work: 'Emergency work',
};

export const overtimeTypeLabel = (code: string | null | undefined) =>
  lookup(OVERTIME_TYPE_LABELS, code);

/** Correction reasons: the shared catalogue, plus the codes the phone sends that it lacks. */
const CORRECTION_REASON_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(CORRECTION_REASON_CATALOG).map(([code, meta]) => [code, meta.label]),
  ),
  forgot_to_time_in: 'Forgot to time in',
  forgot_to_time_out: 'Forgot to time out',
  system_error: 'Application or device issue',
  field_work: 'Field work not recorded',
  wrong_location: 'Recorded at the wrong location',
};

export const correctionReasonLabel = (code: string | null | undefined) =>
  lookup(CORRECTION_REASON_LABELS, code);

const ATTENDANCE_STATUS_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(ATTENDANCE_STATUS_CATALOG).map(([code, meta]) => [code, meta.label]),
  ),
  /*
   * The console's one word for a day in the review queue — the list's
   * "… · awaiting review", the panel's decision, the filter and the history
   * all say it, rather than "Pending review" in some places and "awaiting
   * review" in others for the same state.
   */
  pending_review: 'Awaiting review',
};

export const attendanceStatusLabel = (code: string | null | undefined) =>
  lookup(ATTENDANCE_STATUS_LABELS, code);

const APPROVAL_KIND_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(APPROVAL_TYPE_LABELS).map(([code, meta]) => [code, meta.label]),
);

export const approvalKindLabel = (kind: string | null | undefined) =>
  lookup(APPROVAL_KIND_LABELS, kind);

/**
 * A risk flag as a reviewer reads it.
 *
 * The API stores the employee's sentence ("Your device clock differs from
 * ours; our time was used") because that is what the phone shows. On the HR
 * console that reads as though the screen were talking to the reviewer about
 * their own phone. The shared catalogue carries a reviewer headline and a
 * manager explanation for each code; codes it does not know (the risk engine
 * has a few more) have theirs here.
 */
const EXTRA_FLAGS: Record<string, { label: string; managerExplanation: string }> = {
  selfie_missing: {
    label: 'Selfie missing',
    managerExplanation: 'Policy requires a selfie on time in and none was supplied.',
  },
  signature_invalid: {
    label: 'Device signature did not match',
    managerExplanation: 'The event was signed with a key that does not match the approved device.',
  },
  signature_missing: {
    label: 'Unsigned event',
    managerExplanation: 'The event arrived without the signature an approved device adds.',
  },
};

export function riskFlagForReviewer(
  code: string,
  worded?: { label?: string | null; employeeLabel?: string | null },
): {
  label: string;
  explanation: string | null;
} {
  /*
   * A flag raised on a phone its owner registered and HR had not approved.
   * Titled "Device not registered" above a description saying the employee
   * had registered it, beside a device reading "Waiting for approval", the
   * flag contradicted itself; its headline is the state it describes. The
   * API sends the headline with the wording (`label`); the stored employee
   * sentence identifies it where an older server does not.
   */
  if (
    code === 'unregistered_device' &&
    (worded?.label === PENDING_DEVICE_FLAG.label ||
      worded?.employeeLabel === PENDING_DEVICE_FLAG.employeeLabel)
  ) {
    return { label: PENDING_DEVICE_FLAG.label, explanation: PENDING_DEVICE_FLAG.explanation };
  }
  if (worded?.label) {
    return { label: worded.label, explanation: riskFlagForReviewer(code).explanation };
  }
  const known =
    (RISK_FLAG_CATALOG as Record<string, { label: string; managerExplanation: string }>)[code] ??
    EXTRA_FLAGS[code];
  if (known) return { label: known.label, explanation: known.managerExplanation };
  return { label: humanize(code), explanation: null };
}

/** The unregistered-device flag for a phone waiting for HR's approval. */
const PENDING_DEVICE_FLAG = {
  label: 'Phone waiting for approval',
  employeeLabel: 'Recorded from a phone still waiting for approval',
  explanation:
    'The employee had registered this phone; HR had not approved it yet when this was recorded.',
} as const;

/** Attention-centre groups on the overview. */
const ATTENTION_KIND_LABELS: Record<string, string> = {
  attendance_outside_geofence: 'Flagged attendance',
  pending_correction: 'Attendance corrections',
  leave_pending_hr: 'Leave requests',
  overtime_pending: 'Overtime requests',
  probation_review_due: 'Probation reviews',
  document_expiring: 'Expiring documents',
};

export const attentionKindLabel = (kind: string | null | undefined) =>
  lookup(ATTENTION_KIND_LABELS, kind);

/**
 * Where an attendance record stands with its reviewer.
 *
 * Each outcome has one name, the decision's own: the Decision field, the
 * timeline, the history ("Review: Approved") and the audit diff all read it
 * from here. A return for correction is also the day's status (`rejected`),
 * which the shared status catalogue names the same way.
 */
const REVIEW_STATE_LABELS: Record<string, string> = {
  not_required: 'No review needed',
  pending_review: 'Awaiting review',
  explanation_requested: 'Explanation requested',
  reviewed_approved: 'Approved',
  reviewed_rejected: 'Returned for correction',
};

export const reviewStateLabel = (state: string | null | undefined) =>
  lookup(REVIEW_STATE_LABELS, state);

/** The review states that keep a record in the HR review queue (`inReviewQueue` on the API). */
const OPEN_REVIEW_STATES: ReadonlySet<string> = new Set([
  'pending_review',
  'explanation_requested',
]);

export const isAwaitingReview = (state: string | null | undefined) =>
  !!state && OPEN_REVIEW_STATES.has(state);

/**
 * The statuses that say how a day is going only by its times: `late` and
 * `on_time` are how a day still being worked was started, and
 * `pending_review` / `flagged` are the review marker a held entry parks a day
 * at. None is a status the list shows.
 */
const WORK_BY_TIMES: ReadonlySet<string> = new Set(['late', 'on_time']);
const REVIEW_MARKERS: ReadonlySet<string> = new Set(['pending_review', 'flagged']);

/**
 * The day status a row shows, as a code — the server's `displayedDayStatus`,
 * which its Status filter is defined by, so each option lists exactly the rows
 * that read it. A day at a review marker or at "Late" reads "Completed" once
 * it has a time out and "Working" while it has only a time in; a day parked
 * for review before anything was recorded has none (null).
 */
export function displayedDayStatus(record: {
  status: string;
  timeIn?: string | null;
  timeOut?: string | null;
}): string | null {
  if (WORK_BY_TIMES.has(record.status)) return record.timeOut ? 'completed' : 'working';
  if (REVIEW_MARKERS.has(record.status)) {
    return record.timeOut ? 'completed' : record.timeIn ? 'working' : null;
  }
  return record.status;
}

/**
 * A day's status as a list reads it, review included.
 *
 * The status says how the day went; the review state says whether its
 * evidence has been decided. A completed day still in the review queue read
 * "Completed" beside a decision of "Waiting for review", and the two looked
 * like a contradiction — so a day in the queue says so in the same place.
 *
 * Two halves, two filters: the day status (`displayedDayStatus`) is the
 * Status filter's, and the suffix — "awaiting review" or "explanation
 * requested", from the review state alone — is the Review filter's. A day
 * held for review and one completed first and flagged later both read
 * "Completed · awaiting review" and are listed under both.
 */
export function attendanceStatusWithReview(record: {
  status: string;
  reviewState: string | null | undefined;
  timeIn?: string | null;
  timeOut?: string | null;
}): string {
  const day = displayedDayStatus(record);
  const base = day ? attendanceStatusLabel(day) : null;
  const waiting = reviewSuffix(record.reviewState);
  if (!waiting) return base ?? attendanceStatusLabel(record.status);
  return base ? `${base} · ${waiting}` : `${waiting.charAt(0).toUpperCase()}${waiting.slice(1)}`;
}

/** The review half of a row's status, or null when there is nothing undecided. */
function reviewSuffix(reviewState: string | null | undefined): string | null {
  if (reviewState === 'explanation_requested') return 'explanation requested';
  return isAwaitingReview(reviewState) ? 'awaiting review' : null;
}

/**
 * The registration line under an attendance day's device.
 *
 * The registry's answer when the detail carried one — a day reading
 * "Registered" for a phone nobody could find on the Devices page was the
 * stored flag talking, not the registry. Falls back to that flag only for a
 * record that came without it, and says so when neither was sent.
 *
 * Nothing at all for a day whose entries named no phone: there is no
 * registration to describe, and "Registration not available" beneath it read
 * as an answer that had failed to load.
 */
export function deviceRegistrationHint(record: {
  deviceRegistered: boolean | null;
  deviceRegistration?: 'registered' | 'pending' | 'revoked' | 'none' | null;
  deviceRecorded?: boolean | null;
}): string | undefined {
  if (record.deviceRecorded === false) return undefined;
  switch (record.deviceRegistration) {
    case 'registered':
      return 'Registered';
    case 'pending':
      return 'Waiting for approval';
    case 'revoked':
      return record.deviceRegistered ? 'Registered at the time, since revoked' : 'Revoked';
    case 'none':
      return 'Not registered to this employee';
    default:
      return record.deviceRegistered === null
        ? 'Registration not available'
        : record.deviceRegistered
          ? 'Registered'
          : 'Not registered to this employee';
  }
}

/** "1 day", "2.5 days" — a count in words, without "(s)". */
export function quantity(amount: number | string | null | undefined, unit: string): string {
  const value = Number(amount ?? 0);
  return `${Number.isFinite(value) ? value : 0} ${unit}${value === 1 ? '' : 's'}`;
}

/**
 * A day's time out as a reviewer reads it, where there is none.
 *
 * "9:09 AM – —" said nothing about a day still under way, and nothing
 * different about one that ended without a time out. Today, somebody timed in
 * is still working (or on break); on a past day the time out was never
 * recorded. With no time in there is nothing to end, and the dash stands.
 */
export function timeOutText(record: {
  date: string;
  status: string;
  timeIn: string | null;
  timeOut: string | null;
}): string {
  if (record.timeOut) return formatTime(record.timeOut);
  if (!record.timeIn) return '—';
  if (record.date.slice(0, 10) === todayInBusiness()) {
    return record.status === 'on_break' ? 'On break' : 'Still working';
  }
  // A past day is over: "yet" would promise a time out still to come.
  return 'No time out';
}

/** "9:09 AM – 6:02 PM", "9:09 AM – still working", "9:09 AM – no time out". */
export function attendanceTimeRange(record: {
  date: string;
  status: string;
  timeIn: string | null;
  timeOut: string | null;
}): string {
  if (!record.timeIn) return record.timeOut ? `No time in – ${formatTime(record.timeOut)}` : '—';
  if (record.timeOut) return `${formatTime(record.timeIn)} – ${formatTime(record.timeOut)}`;
  const open = timeOutText(record);
  return `${formatTime(record.timeIn)} – ${open.charAt(0).toLowerCase()}${open.slice(1)}`;
}

/**
 * Why a record in the review queue was held, as one sentence.
 *
 * The queue listed a score of 25 while Settings said "at or above 40 needs a
 * person". The reason is one of three, and the sentence names which: a check
 * that always needs a person, an entry that reached the threshold, or a rule
 * that has changed since the entry was held.
 */
export function reviewHoldText(hold: {
  rule: 'always_needs_person' | 'score_at_threshold' | 'earlier_rule';
  flagLabels: string[];
  entryLabel: string | null;
  entryScore: number;
  reviewThreshold: number;
}): string {
  const checks = listInWords(hold.flagLabels.map((label) => `“${label}”`));
  const entry = hold.entryLabel ? `the ${hold.entryLabel.toLowerCase()}` : 'an entry';
  switch (hold.rule) {
    case 'always_needs_person':
      return `Held for review because ${checks} always ${hold.flagLabels.length === 1 ? 'needs' : 'need'} a person, whatever the score.`;
    case 'score_at_threshold':
      return `Held for review because ${entry} scored ${hold.entryScore} (${checks}), at or above your review threshold of ${hold.reviewThreshold}.`;
    case 'earlier_rule':
      return `Held for review under an earlier rule: ${entry} scored ${hold.entryScore} (${checks}), below today's review threshold of ${hold.reviewThreshold}, so an entry like it recorded now would count without review. It still needs a decision.`;
  }
}

/** "a", "a and b", "a, b and c". */
function listInWords(items: string[]): string {
  if (items.length <= 1) return items[0] ?? 'a check';
  return `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
}
